import { computed, inject, type ComputedRef } from 'vue'
import { cloneDeep, merge } from 'lodash-es'
import { useI18n } from 'vue-i18n'

import useCanvas from '@canvas-builder/composables/useCanvas'
import { doesLocalOverrideExist, getLocalOverrideUrl } from '@lib/util/dev.util'
import { ComponentTypes, type SectionListProps, type ComponentNode } from '@canvas-builder/types/canvas'
import type { AppFile } from '@lib/types/app'
import type { WizardStep } from '@canvas-builder/components/container/CanvasCompletionWizard/CanvasCompletionWizard.vue'

export default function useCanvasCompletionWizard() {
  const { t } = useI18n()
  const { activeCanvas, saveCanvasContentWithContext, isImpact, canvasContent } = useCanvas()
  const sectionSelectorApps = inject<ComputedRef<AppFile[]>>(
    'sectionSelectorApps',
    computed(() => []),
  )

  const sectionListNodes = computed<ComponentNode[]>(() =>
    canvasContent.value.filter((node) => {
      if (![ComponentTypes.SectionList, ComponentTypes.ContentList].includes(node.type)) return false
      const data = node.data as SectionListProps
      // Skip if strategy is free or if strategy is app but there's no valid app source
      if (data.selection_strategy === 'free') return false
      if (data.selection_strategy === 'app' && data.selection_app_name) {
        const appExists = sectionSelectorApps.value.some(
          (app) =>
            app.app_metadata?.name === data.selection_app_name &&
            (app.content_url || (app.app_metadata?.name && doesLocalOverrideExist(app.app_metadata.name))),
        )
        if (!appExists) return false
      }
      return !!data?.selection_strategy
    }),
  )

  const wizardSteps = computed<WizardStep[]>(() => {
    return sectionListNodes.value.map((node) => {
      const data = node.data as SectionListProps
      const appSrc =
        data.selection_strategy === 'app' && data.selection_app_name ? getAppSrc(data.selection_app_name) : null
      const appDescription =
        data.selection_strategy === 'app' && data.selection_app_name
          ? sectionSelectorApps.value.find((app) => app.app_metadata?.name === data.selection_app_name)?.app_metadata
              ?.description
          : ''

      // Convert old strategy types to new ones if needed
      let strategy = data.selection_strategy || 'free'
      // Cast as any to avoid type checking during conversion
      if (!['free', 'single', 'at-least-one', 'app'].includes(strategy)) strategy = 'free'

      return {
        id: node.id,
        name: node.tracking_id !== node.type ? node.tracking_id : undefined,
        strategy,
        strategyLabel: getStrategyLabel(strategy),
        appDescription,
        appSrc,
        selectedSectionIds: (data.sections || []).map((section) => section.id),
        componentNode: node,
        is_restricted_to_preselections: data.is_restricted_to_preselections,
      }
    })
  })

  function getStrategyLabel(strategy?: string): string {
    if (strategy === 'single') return t('canvasUI.strategy.singleSection')
    if (strategy === 'at-least-one') return t('canvasUI.strategy.atLeastOne')
    if (strategy === 'app') return t('canvasUI.strategy.customApp')
    return t('canvasUI.strategy.free')
  }

  function getAppSrc(appName: string): string | null {
    const app = sectionSelectorApps.value.find((app) => app.app_metadata?.name === appName)
    if (!app) return null

    try {
      if (app.app_metadata?.name && doesLocalOverrideExist(app.app_metadata.name)) {
        return getLocalOverrideUrl(app.app_metadata.name) as string
      }
    } catch (e) {
      console.error('Error generating Embeddable debug url', e)
    }

    return app.content_url || null
  }
  const isCompletionWizardEnabled = computed(() => wizardSteps.value.length > 0)
  const isCompletionWizardCompleted = computed(
    () => !!activeCanvas.value?.context?.pitcher?.completion_wizard?.completed,
  )
  const isCompletionWizardShowing = computed(
    () => isCompletionWizardEnabled.value && !isCompletionWizardCompleted.value,
  )

  /**
   * Retrigger the completion wizard by clearing its completion status
   * This will make the wizard appear again for the current canvas
   */
  async function retriggerWizard({ sectionListId }: { sectionListId?: string } = {}): Promise<void> {
    if (!isImpact.value) {
      console.warn('[useCanvasCompletionWizard] The wizard can only be retriggered on Impact')
      return
    } else if (!activeCanvas.value) {
      console.warn('[useCanvasCompletionWizard] No active canvas found')
      return
    } else if (!isCompletionWizardEnabled.value) {
      console.warn('[useCanvasCompletionWizard] No steps to complete. Bailing on retriggering...')
      return
    }

    try {
      const currentContext = cloneDeep(activeCanvas.value.context)
      const updatedContext = merge(currentContext || {}, {
        pitcher: {
          completion_wizard: {
            ...currentContext?.pitcher?.completion_wizard,
            completed: false,
            edit_triggered_by_section_list_id: sectionListId,
            edited: true, // Mark as edited to distinguish from initial completion
          },
        },
      })
      const meta = merge(cloneDeep(activeCanvas.value?.metadata ?? {}), {
        pitcher: { completion_wizard: { completed: false } },
      })

      await saveCanvasContentWithContext(updatedContext, { showToast: false }, meta)
    } catch (error) {
      console.error('[useCanvasCompletionWizard] Failed to retrigger wizard:', error)
      throw error
    }
  }

  /**
   * Check if the wizard can be retriggered
   */
  function canRetriggerWizard(): boolean {
    return !!(activeCanvas.value && isCompletionWizardEnabled.value && isCompletionWizardCompleted.value)
  }

  /**
   * Reset the wizard completely by clearing all completion data
   */
  async function resetWizard(): Promise<void> {
    if (!activeCanvas.value) {
      console.warn('[useCanvasCompletionWizard] No active canvas found')
      return
    }

    try {
      const currentContext = cloneDeep(activeCanvas.value.context || {})

      // Remove the entire completion_wizard object to reset everything
      if (currentContext?.pitcher?.completion_wizard) {
        delete currentContext.pitcher.completion_wizard
      }

      await saveCanvasContentWithContext(currentContext, { showToast: false })

      // Force a page reload to ensure the wizard appears fresh
      window.location.reload()
    } catch (error) {
      console.error('[useCanvasCompletionWizard] Failed to reset wizard:', error)
      throw error
    }
  }

  return {
    wizardSteps,
    sectionListNodes,
    isCompletionWizardEnabled,
    isCompletionWizardCompleted,
    isCompletionWizardShowing,
    retriggerWizard,
    canRetriggerWizard,
    resetWizard,
    getStrategyLabel,
  }
}
